//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
import { ArrowBackIcon, QuestionIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React from "react";
import { number, object } from "yup";
import { formatCurrency, formatPrice } from "../../../utils/functions";

const commonProps = ({ name }, { touched, errors }) => ({
  label: "Enter price for 2 students",
  placeholder: "Type a price",
  isInvalid: Boolean(touched[name] && errors[name]),
  errorMessage: errors[name],
});

export const SetNewDiscount: React.FC<{
  initialValues: { extraStudents: number };
  onBackClick: () => void;
  hourlyRate?: string;
  isLoading?: boolean;
  currency: string;
  subject?: string;
  onSubmit: (values: any) => void;
}> = observer(
  ({
    onBackClick,
    initialValues,
    hourlyRate,
    subject,
    isLoading,
    onSubmit,
  }) => {
    const [showBreakdown, setShowBreakdown] = React.useState(false);
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    const monthlyPrice = Number(hourlyRate) * 24;
    const suggestedPrice = monthlyPrice * 1.2;

    const validationSchema = object().shape({
      extraStudents: number()
        .min(
          Number(monthlyPrice),
          `Must be at least ${formatPrice(monthlyPrice)}`,
        )
        .max(
          Number(monthlyPrice) * 2,
          `Can't be more than ${formatPrice(monthlyPrice * 2)}`,
        )
        .required("Enter a price above"),
    });

    const getExtraStudentDiscount = (price) => {
      return Math.round((Number(price) / monthlyPrice - 1) * 100);
    };

    return (
      <Box className="set-new-discount">
        <Button
          mb={6}
          onClick={onBackClick}
          leftIcon={<ArrowBackIcon />}
          variant="link"
          colorScheme="blue"
        >
          Back
        </Button>

        {!hourlyRate && (
          <Alert status="error" mb={6} rounded="md">
            <AlertIcon />
            <AlertTitle>You haven't set an hourly rate</AlertTitle>
          </Alert>
        )}

        <Stack pb={6}>
          <Text fontSize="3xl" lineHeight="shorter" fontWeight="bold" as="h1">
            Extra students rate
          </Text>
          <Text mt={1} color="gray.700">
            Set rate for each additional {subject} student by answering the
            question below:
          </Text>
        </Stack>

        {hourlyRate ? (
          <Stack
            p={[5, 6]}
            borderWidth="2xs"
            bg="blue.50"
            borderColor="blue.200"
            rounded="md"
            mb={6}
          >
            <HStack mb={2}>
              <QuestionIcon fontSize="xl" />
              <Text fontSize="lg" fontWeight="semibold">
                Question
              </Text>
            </HStack>

            <Text>Your hourly rate is {formatPrice(hourlyRate)}.</Text>

            <Text>
              This means clients will pay {formatPrice(monthlyPrice)} per month
              to book 2-hour lessons, thrice a week for 1 student.
            </Text>

            <Text>
              How much would you charge for 2 students, same schedule?
            </Text>
          </Stack>
        ) : null}

        <Formik
          initialValues={{
            extraStudents: initialValues.extraStudents
              ? monthlyPrice * (initialValues.extraStudents / 100 + 1)
              : 0,
          }}
          onSubmit={(values) => {
            const extraStudentsDiscount = getExtraStudentDiscount(
              values.extraStudents,
            );
            onSubmit({ extraStudentsDiscount });
          }}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, values, errors }) => {
            const disabledButton = showConfirmation
              ? true
              : Boolean(!values.extraStudents);
            return (
              <Form>
                <Stack mb={8} spacing={8}>
                  <Field
                    name="extraStudents"
                    render={({ field, form }) => {
                      return (
                        <Stack spacing="6">
                          <FormControl
                            isInvalid={commonProps(field, form).isInvalid}
                          >
                            <FormLabel>
                              {commonProps(field, form).label}
                            </FormLabel>
                            <Box
                              borderWidth="2xs"
                              borderColor="neutral.500"
                              rounded="md"
                              overflow="hidden"
                            >
                              <Flex>
                                <Input
                                  height="3.5rem"
                                  size="lg"
                                  flex="5"
                                  opacity="1"
                                  rounded="none"
                                  border="none"
                                  fontSize="xl"
                                  fontWeight={field.value ? "medium" : "normal"}
                                  borderColor="neutral.700"
                                  {...field}
                                  {...commonProps(field, form)}
                                  name={field.name}
                                  value={formatPrice(field.value)}
                                  onBlur={() => setShowConfirmation(true)}
                                  onChange={({ target }) => {
                                    form.setFieldValue(
                                      field.name,
                                      formatCurrency(target.value, "number"),
                                    );
                                    form.setFieldValue("confirmation", []);
                                    setShowConfirmation(false);
                                  }}
                                />

                                <Button
                                  flex={1}
                                  bg="white"
                                  shadow="none"
                                  color="blue.500"
                                  isDisabled={disabledButton}
                                  borderLeftWidth="2xs"
                                  borderColor="neutral.500"
                                  roundedLeft="none"
                                  roundedBottomRight="none"
                                  height="3.5rem"
                                  onClick={() => {
                                    setShowConfirmation(true);
                                    setShowBreakdown(false);
                                  }}
                                >
                                  Done
                                </Button>
                              </Flex>

                              {hourlyRate ? (
                                <Stack
                                  px={4}
                                  py={3}
                                  bg="neutral.200"
                                  borderTopWidth="2xs"
                                  borderColor="neutral.500"
                                >
                                  <HStack justifyContent="space-between">
                                    <Box>
                                      <Text fontWeight="light" fontSize="xs">
                                        Suggestion
                                      </Text>
                                      <HStack>
                                        <Text>
                                          {formatPrice(suggestedPrice)}
                                        </Text>
                                        <QuestionIcon
                                          cursor="pointer"
                                          onClick={() =>
                                            setShowBreakdown(!showBreakdown)
                                          }
                                        />
                                      </HStack>
                                    </Box>

                                    <Button
                                      bg="black"
                                      size="sm"
                                      color="white"
                                      rounded="md"
                                      onClick={() => {
                                        form.setFieldValue(
                                          field.name,
                                          suggestedPrice,
                                        );
                                        setShowConfirmation(true);
                                        setShowBreakdown(false);
                                      }}
                                    >
                                      Use this
                                    </Button>
                                  </HStack>

                                  <Collapse in={showBreakdown}>
                                    <Box pb={2}>
                                      This is a 20% increase for each additional
                                      student.
                                    </Box>

                                    <Box pb={2}>
                                      We've found that the most successful
                                      tutors charge extra 10% - 30% of the
                                      standard price for each additional student
                                      in the lesson.
                                    </Box>

                                    <Text>
                                      This is only a suggestion. You can charge
                                      any price that best reflects your value.
                                    </Text>
                                  </Collapse>
                                </Stack>
                              ) : null}
                            </Box>

                            <FormErrorMessage>
                              {commonProps(field, form).errorMessage}
                            </FormErrorMessage>
                          </FormControl>
                        </Stack>
                      );
                    }}
                  />

                  {showConfirmation &&
                    values?.extraStudents &&
                    !errors?.extraStudents && (
                      <Stack
                        p={[5, 6]}
                        borderWidth="2xs"
                        borderColor="neutral.500"
                        rounded="md"
                      >
                        <Text>You're charging</Text>
                        <Text fontWeight="semibold" fontSize="4xl">
                          {getExtraStudentDiscount(values.extraStudents)}%
                        </Text>

                        <Text>
                          extra fee for each additional student. This will apply
                          from the second student when you're booked for
                          multiple students.
                        </Text>

                        <Text>Do you wish to proceed?</Text>
                      </Stack>
                    )}

                  <Button
                    colorScheme="blue"
                    size="lg"
                    isLoading={isLoading}
                    loadingText="Saving"
                    isDisabled={!hourlyRate}
                    onClick={() => handleSubmit()}
                  >
                    Set extra student rate
                  </Button>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Box>
    );
  },
);
